import { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import {
  useGetRelationshipsQuery,
  useGetItemStatusesQuery,
  useGetTeamsQuery,
  useGetTagsQuery,
} from 'store/api';
import {
  ColorTypographyCell,
  MailMenuCell,
  RowCell,
  TabIndexTable,
  BackdropLoading,
  AvatarsCell,
  LinkCell,
  WARNING_DAYS,
  IconCircleCell,
  ChipButtonMenuCell,
  DateChipBadgeCell,
} from '@centerpiece/components';
import { Box } from '@mui/material';
import { ItemStatus } from 'utils/status.utils';
import { useTutorialContext } from 'tutorials/tutorial.context';
import { useItemsMsgs, useItemsCmts, useItemIndex } from 'utils/index.utils';
import { useIndexContext } from 'contexts/index.context';
import { ItemTabDrawer } from 'drawers/ItemTabDrawer';
import { useNavigate } from 'react-router-dom';

export const ItemsIndexPage = () => {
  const navigate = useNavigate();
  const { contactRecord, cmts, msgs, refetchCount, refetchIndex } =
    useIndexContext();
  // BEGIN USESTATE
  const [itemIndex, setItemIndex] = useState<{ [key: number]: any }>({});
  const toDoSummaries = useMemo(() => {
    return Object.values(itemIndex).filter(i => i.id);
  }, [itemIndex]);
  const [selectedTag, setSelectedTag] = useState<{ name: string; id: number }>({
    name: 'All Tags',
    id: -1,
  });
  const [itemOwnershipFilter, setItemOwnershipFilterActual] =
    useState<ItemStatus>({
      id: -2,
      name: 'All Items',
    } as ItemStatus); // active
  const setItemOwnershipFilter = (status: ItemStatus) => {
    if (status.id === -2 || status.id > 0) {
      setItemFilter({ id: -2, name: 'At Risk' });
    }
    setItemOwnershipFilterActual(status);
  };
  const [itemFilter, setItemFilter] = useState<ItemStatus>({
    id: -1,
    name: 'Active',
  } as ItemStatus); // active
  const [activeItem, setActiveItem] = useState<any>();
  // END USESTATE
  // eslint-disable-next-line

  useEffect(() => refetchRelationships(), [refetchCount]);

  /////// BEGIN QUERIES
  const { data: statuses = [] } = useGetItemStatusesQuery({});
  const { data: teams = [] } = useGetTeamsQuery({});
  const { data: tags = [] } = useGetTagsQuery({});
  const {
    data: relationships = [],
    isLoading: relationshipsLoading,
    isFetching: relationshipsFetching,
    refetch: refetchRelationships,
  } = useGetRelationshipsQuery({
    params:
      itemFilter?.id === -1
        ? {
            status: `!3`,
            rollup: 'all',
            afs:
              ((contactRecord?.form?.authorization === 'Executive' ||
                contactRecord?.form?.authorization === 'Organization Member') &&
                itemOwnershipFilter.id !== -1) ||
              ((contactRecord?.form?.authorization === 'Team Leader' ||
                contactRecord?.form?.authorization === 'Team Member') &&
                itemOwnershipFilter.id > 0)
                ? true
                : undefined,
            team_ids:
              itemOwnershipFilter.id > 0 ? itemOwnershipFilter.id : undefined,
            tag_ids: selectedTag.id > 0 ? selectedTag.id : undefined,
          }
        : itemFilter?.id === -2
        ? {
            afs:
              ((contactRecord?.form?.authorization === 'Executive' ||
                contactRecord?.form?.authorization === 'Organization Member') &&
                itemOwnershipFilter.id !== -1) ||
              ((contactRecord?.form?.authorization === 'Team Leader' ||
                contactRecord?.form?.authorization === 'Team Member') &&
                itemOwnershipFilter.id > 0)
                ? true
                : undefined,
            status: `!3`, // when getting all at risk only get active items
            rollup: 'all',
            team_ids:
              itemOwnershipFilter.id > 0 ? itemOwnershipFilter.id : undefined,
            tag_ids: selectedTag.id > 0 ? selectedTag.id : undefined,
          }
        : {
            afs:
              ((contactRecord?.form?.authorization === 'Executive' ||
                contactRecord?.form?.authorization === 'Organization Member') &&
                itemOwnershipFilter.id !== -1) ||
              ((contactRecord?.form?.authorization === 'Team Leader' ||
                contactRecord?.form?.authorization === 'Team Member') &&
                itemOwnershipFilter.id > 0)
                ? true
                : undefined,
            status: itemFilter.id,
            rollup: 'all',
            team_ids:
              itemOwnershipFilter.id > 0 ? itemOwnershipFilter.id : undefined,
            tag_ids: selectedTag.id > 0 ? selectedTag.id : undefined,
          },
  });
  /////// END QUERIES
  const { state, setState } = useTutorialContext();
  useEffect(() => {
    if (state.tourActive) {
      setState(currState => ({
        ...currState,
        run: true,
      }));
    }
    // eslint-disable-next-line
  }, []);
  useItemIndex({ relationships, setItemIndex });
  useItemsMsgs({ msgs, setItemIndex });
  useItemsCmts({ cmts, setItemIndex });

  useEffect(() => {
    if (contactRecord?.form?.authorization === 'Executive') {
      setItemOwnershipFilter({ id: -2, name: 'All Items' } as any);
      setItemFilter({ id: -2, name: 'At Risk' });
    } else if (
      contactRecord?.form?.authorization === 'Team Leader' &&
      contactRecord?.form?.team
    ) {
      setItemOwnershipFilter(contactRecord?.form?.team as any);
      setItemFilter({ id: -2, name: 'At Risk' });
    } else {
      setItemOwnershipFilter({ id: -1, name: 'My Items' } as any);
    }
    // eslint-disable-next-line
  }, [contactRecord]);

  return (
    <>
      {activeItem?.item_type &&
        ['coordination', 'task', 'event', 'item'].includes(
          activeItem.item_type
        ) && (
          <ItemTabDrawer
            open={true}
            transparentBackdrop={true}
            id={activeItem?.id}
            project_id={activeItem?.project_r?.id}
            afterSave={() => {
              refetchIndex(); //calls refreshRelationships eventually
            }}
            onClose={() => {
              setActiveItem(undefined);
            }}
            breadcrumbs={[
              {
                label: activeItem?.project_title?.text,
                onClick: () =>
                  navigate(`/project/${activeItem?.project_r?.id}`),
              },
              {
                label: activeItem.title,
                onClick: () =>
                  navigate(
                    `/project/${activeItem?.project_r?.id}#i=${activeItem?.item}`
                  ),
              },
            ]}
          />
        )}
      <Box
        id={'tab-index-table'}
        sx={{ pt: { xs: 4, md: 0 }, pb: { xs: 15, md: 10 } }}
      >
        {relationshipsLoading && <BackdropLoading />}
        <TabIndexTable
          filterTitle="Item"
          accordian={false} //calendar accordian at the bottome
          initialTab={0}
          tables={useMemo(
            () => [
              {
                itemBorder: true,
                title: 'Items',
                key: 'items',
                visible: true,
                filters: [
                  {
                    label: 'Items',
                    options:
                      contactRecord?.form?.authorization === 'Team Leader' ||
                      contactRecord?.form?.authorization === 'Team Member'
                        ? [
                            { id: -1, name: 'My Items' },
                            ...(contactRecord?.form?.team
                              ? [
                                  teams.find(
                                    t => t.id == contactRecord?.form?.team?.id
                                  ),
                                ]
                              : []),
                          ]
                        : contactRecord?.form?.authorization === 'Executive' ||
                          contactRecord?.form?.authorization ===
                            'Organization Member'
                        ? [
                            { id: -1, name: 'My Items' },
                            { id: -2, name: 'All Items' },
                            ...(teams || []),
                          ]
                        : [{ id: -1, name: 'My Items' }],
                    defaultOption: itemOwnershipFilter,
                    onChange: (event, newValue) =>
                      setItemOwnershipFilter(newValue),
                    loading: relationshipsFetching,
                  },
                  {
                    label: 'Status',
                    options:
                      itemOwnershipFilter.id === -2 ||
                      itemOwnershipFilter.id > 0
                        ? [{ id: -2, name: 'At Risk' }]
                        : [
                            { id: -1, name: 'Active' },
                            ...statuses,
                            { id: -2, name: 'At Risk' },
                          ],
                    defaultOption: itemFilter,
                    onChange: (event, newValue) => setItemFilter(newValue),
                    loading: relationshipsFetching,
                  },
                  {
                    options: [{ id: -1, name: 'All Tags' }, ...(tags || [])],
                    label: 'Tag',
                    defaultOption: selectedTag,
                    onChange: (event: any, newValue: any) =>
                      setSelectedTag(newValue),
                    loading: relationshipsFetching,
                  },
                ],
                defaultOrder: 'asc',
                defaultOrderBy: 'dates.due',
                headCells: [
                  {
                    name: 'type',
                    label: 'Type',
                    checkboxFilter: 'type.type',
                    component: IconCircleCell,
                  },
                  {
                    name: 'hoverTitle',
                    label: 'Title',
                    textFilter: 'hoverTitle.text',
                    sort: 'hoverTitle.text',
                    component: ColorTypographyCell,
                  },
                  {
                    name: 'project_title',
                    label: 'Project',
                    align: 'left',
                    textFilter: 'project_title.text',
                    sort: 'project_title.text',
                    component: LinkCell,
                  },
                  {
                    name: 'msgs',
                    label: '',
                    align: 'right',
                    disablePadding: true,
                    sx: { width: '75px' },
                    component: MailMenuCell,
                    render: (row: any, name: any): any => {
                      return {
                        msgs: row.msgs,
                        counts: row.counts,
                        cmts: row.cmts,
                      };
                    },
                  },
                  {
                    name: 'team',
                    label: 'Team',
                    disablePadding: true,
                    checkboxFilter: 'team.team',
                    sx: { width: '50px' },
                    component: AvatarsCell,
                  },

                  {
                    name: 'dates',
                    label: 'Item Date(s)',
                    sort: 'dates.due',
                    sx: { width: '120px' },
                    disablePadding: true,
                    component: DateChipBadgeCell,
                  },
                  {
                    name: 'chipStatus',
                    label: 'Status',
                    align: 'right',
                    sx: { width: '150px' },
                    disablePadding: true,
                    clearFilter: true,
                    component: ChipButtonMenuCell,
                  },
                ],
                onRowClick: (event, row) => {
                  setActiveItem({ ...row, icon: null });
                },
                rows:
                  itemFilter.id === -2
                    ? (toDoSummaries || ([] as RowCell[]))?.filter(
                        (item: any) =>
                          item?.due_date &&
                          moment(item?.due_date).diff(moment.now(), 'days') <=
                            WARNING_DAYS
                      )
                    : toDoSummaries || ([] as RowCell[]),
              },
            ],
            [
              toDoSummaries,
              itemFilter,
              contactRecord,
              selectedTag,
              itemOwnershipFilter,
              relationshipsFetching,
            ]
          )}
        />
      </Box>
    </>
  );
};
